import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  TextField, 
  Button, 
  CircularProgress, 
  Typography, 
  Paper, 
  Box,
  Alert,
  Divider,
  Chip,
  Avatar
} from '@mui/material';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaTicketAlt, 
  FaCheck, 
  FaTimes, 
  FaUser, 
  FaCalendarAlt, 
  FaClock,
  FaQrcode,
  FaSearch
} from 'react-icons/fa';

const Container = styled.div`
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: white;
  font-size: 2rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #4b7bec;
  }
`;

const StyledPaper = styled(Paper)`
  padding: 2rem;
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-radius: 12px !important;
  margin-bottom: 2rem;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StyledTextField = styled(TextField)`
  flex: 1;
  
  .MuiInputBase-root {
    color: white !important;
  }
  
  .MuiInputLabel-root {
    color: rgba(255, 255, 255, 0.7) !important;
  }
  
  .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.2) !important;
  }
  
  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(255, 255, 255, 0.5) !important;
  }
  
  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #4b7bec !important;
  }
`;

const TicketCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  margin-top: 2rem;
`;

const TicketHeader = styled.div`
  background: ${props => props.isValid ? 'rgba(46, 204, 113, 0.2)' : props.isInvalid ? 'rgba(231, 76, 60, 0.2)' : 'rgba(75, 123, 236, 0.2)'};
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const TicketStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    font-size: 1.5rem;
  }
`;

const TicketBody = styled.div`
  padding: 1.5rem;
`;

const TicketInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoLabel = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: #4b7bec;
  }
`;

const InfoValue = styled.div`
  color: white;
  font-size: 1rem;
  font-weight: 500;
`;

const TicketActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Instructions = styled.div`
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 0.7);
  
  ul {
    padding-left: 1.5rem;
    margin-top: 1rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const TicketVerification = () => {
  const { currentUser } = useAuth();
  const [ticketCode, setTicketCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ticket, setTicket] = useState(null);
  const [event, setEvent] = useState(null);
  const [user, setUser] = useState(null);
  const [verifying, setVerifying] = useState(false);
  
  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!ticketCode.trim()) {
      setError('Please enter a ticket code');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setTicket(null);
      setEvent(null);
      setUser(null);
      
      // Search for the ticket by access code
      const ticketsQuery = query(
        collection(db, 'tickets'),
        where('accessCode', '==', ticketCode.trim())
      );
      
      const ticketsSnapshot = await getDocs(ticketsQuery);
      
      if (ticketsSnapshot.empty) {
        setError('Ticket not found. Please check the code and try again.');
        return;
      }
      
      const ticketDoc = ticketsSnapshot.docs[0];
      const ticketData = ticketDoc.data();
      
      // Fetch the event details
      const eventDoc = await getDoc(doc(db, 'streamEvents', ticketData.eventId));
      
      if (!eventDoc.exists()) {
        setError('Event not found for this ticket.');
        return;
      }
      
      const eventData = eventDoc.data();
      
      // Check if the current user is the event creator
      if (eventData.creatorId !== currentUser.uid) {
        setError('You do not have permission to verify tickets for this event.');
        return;
      }
      
      // Fetch user details if available
      let userData = null;
      if (ticketData.userId) {
        const userDoc = await getDoc(doc(db, 'users', ticketData.userId));
        if (userDoc.exists()) {
          userData = userDoc.data();
        }
      }
      
      setTicket({
        id: ticketDoc.id,
        ...ticketData
      });
      setEvent(eventData);
      setUser(userData);
      
    } catch (error) {
      console.error('Error searching for ticket:', error);
      setError('An error occurred while searching for the ticket.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleVerifyTicket = async () => {
    if (!ticket) return;
    
    try {
      setVerifying(true);
      
      // Update the ticket status to verified
      await updateDoc(doc(db, 'tickets', ticket.id), {
        status: 'verified',
        verifiedAt: serverTimestamp(),
        verifiedBy: currentUser.uid
      });
      
      // Update the local ticket state
      setTicket({
        ...ticket,
        status: 'verified',
        verifiedAt: new Date(),
        verifiedBy: currentUser.uid
      });
      
    } catch (error) {
      console.error('Error verifying ticket:', error);
      setError('Failed to verify ticket.');
    } finally {
      setVerifying(false);
    }
  };
  
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  const formatTime = (timeString) => {
    return timeString;
  };
  
  return (
    <Container>
      <Title>
        <FaTicketAlt /> Ticket Verification
      </Title>
      
      <StyledPaper>
        <Instructions>
          <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
            Instructions
          </Typography>
          <Typography variant="body2">
            Use this tool to verify tickets for your events. Enter the ticket code provided by the attendee.
          </Typography>
          <ul>
            <li>Ask the attendee for their ticket code or scan their QR code</li>
            <li>Enter the code in the search box below</li>
            <li>Verify the ticket details and attendee information</li>
            <li>Click "Verify Ticket" to mark the ticket as used</li>
          </ul>
        </Instructions>
        
        <SearchForm onSubmit={handleSearch}>
          <StyledTextField
            label="Enter Ticket Code"
            value={ticketCode}
            onChange={(e) => setTicketCode(e.target.value)}
            placeholder="e.g., ABCD-1234-EFGH"
            fullWidth
            InputProps={{
              startAdornment: <FaQrcode style={{ marginRight: '10px', color: '#4b7bec' }} />
            }}
          />
          
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <FaSearch />}
            sx={{
              backgroundColor: '#4b7bec',
              '&:hover': { backgroundColor: '#3867d6' },
              minWidth: '120px'
            }}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </SearchForm>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(211, 47, 47, 0.1)' }}>
            {error}
          </Alert>
        )}
        
        {ticket && event && (
          <TicketCard>
            <TicketHeader isValid={ticket.status === 'active' || ticket.status === 'verified'} isInvalid={ticket.status === 'cancelled' || ticket.status === 'expired'}>
              <Typography variant="h6" sx={{ color: 'white' }}>
                Ticket #{ticket.id.substring(0, 8)}
              </Typography>
              
              <TicketStatus>
                {ticket.status === 'active' && (
                  <>
                    <Chip
                      label="Active"
                      sx={{ backgroundColor: 'rgba(46, 204, 113, 0.2)', color: '#2ecc71' }}
                    />
                  </>
                )}
                
                {ticket.status === 'verified' && (
                  <>
                    <FaCheck style={{ color: '#2ecc71' }} />
                    <Chip
                      label="Verified"
                      sx={{ backgroundColor: 'rgba(46, 204, 113, 0.2)', color: '#2ecc71' }}
                    />
                  </>
                )}
                
                {ticket.status === 'pending' && (
                  <>
                    <Chip
                      label="Pending"
                      sx={{ backgroundColor: 'rgba(243, 156, 18, 0.2)', color: '#f39c12' }}
                    />
                  </>
                )}
                
                {ticket.status === 'cancelled' && (
                  <>
                    <FaTimes style={{ color: '#e74c3c' }} />
                    <Chip
                      label="Cancelled"
                      sx={{ backgroundColor: 'rgba(231, 76, 60, 0.2)', color: '#e74c3c' }}
                    />
                  </>
                )}
                
                {ticket.status === 'expired' && (
                  <>
                    <FaTimes style={{ color: '#e74c3c' }} />
                    <Chip
                      label="Expired"
                      sx={{ backgroundColor: 'rgba(231, 76, 60, 0.2)', color: '#e74c3c' }}
                    />
                  </>
                )}
              </TicketStatus>
            </TicketHeader>
            
            <TicketBody>
              <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                {event.title}
              </Typography>
              
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
              
              <TicketInfo>
                <InfoItem>
                  <InfoLabel>
                    <FaCalendarAlt />
                    Date
                  </InfoLabel>
                  <InfoValue>{formatDate(event.date)}</InfoValue>
                </InfoItem>
                
                <InfoItem>
                  <InfoLabel>
                    <FaClock />
                    Time
                  </InfoLabel>
                  <InfoValue>{formatTime(event.time)}</InfoValue>
                </InfoItem>
                
                <InfoItem>
                  <InfoLabel>
                    <FaTicketAlt />
                    Ticket Type
                  </InfoLabel>
                  <InfoValue>
                    {ticket.ticketType === 'premium' ? 'Premium' : 'Standard'}
                  </InfoValue>
                </InfoItem>
                
                <InfoItem>
                  <InfoLabel>
                    <FaUser />
                    Attendee
                  </InfoLabel>
                  <InfoValue>
                    {ticket.contactInfo?.name || (user ? user.displayName : 'Anonymous')}
                  </InfoValue>
                </InfoItem>
              </TicketInfo>
              
              <Box sx={{ mt: 3, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
                  Contact Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Email: {ticket.contactInfo?.email || (user ? user.email : 'N/A')}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Phone: {ticket.contactInfo?.phone || 'N/A'}
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Access Code
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'white', letterSpacing: '1px' }}>
                    {ticket.accessCode}
                  </Typography>
                </Box>
                
                <TicketActions>
                  {ticket.status === 'active' && (
                    <Button
                      variant="contained"
                      startIcon={verifying ? <CircularProgress size={20} /> : <FaCheck />}
                      onClick={handleVerifyTicket}
                      disabled={verifying}
                      sx={{
                        backgroundColor: '#2ecc71',
                        '&:hover': { backgroundColor: '#27ae60' }
                      }}
                    >
                      {verifying ? 'Verifying...' : 'Verify Ticket'}
                    </Button>
                  )}
                  
                  {ticket.status === 'verified' && (
                    <Button
                      variant="contained"
                      startIcon={<FaCheck />}
                      disabled
                      sx={{
                        backgroundColor: 'rgba(46, 204, 113, 0.2)',
                        color: '#2ecc71',
                        '&.Mui-disabled': {
                          color: '#2ecc71',
                          backgroundColor: 'rgba(46, 204, 113, 0.2)'
                        }
                      }}
                    >
                      Already Verified
                    </Button>
                  )}
                  
                  {(ticket.status === 'cancelled' || ticket.status === 'expired') && (
                    <Button
                      variant="contained"
                      startIcon={<FaTimes />}
                      disabled
                      sx={{
                        backgroundColor: 'rgba(231, 76, 60, 0.2)',
                        color: '#e74c3c',
                        '&.Mui-disabled': {
                          color: '#e74c3c',
                          backgroundColor: 'rgba(231, 76, 60, 0.2)'
                        }
                      }}
                    >
                      Invalid Ticket
                    </Button>
                  )}
                </TicketActions>
              </Box>
            </TicketBody>
          </TicketCard>
        )}
      </StyledPaper>
    </Container>
  );
};

export default TicketVerification;
